import React, { useCallback, useEffect, useMemo, useState } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { triggerToast } from "../../utils/toast";
import { axiosInstance } from "../../utils/axiosInstance";
import { formatFilterValue } from "../../utils/formatFilters";
import { Pagination } from "cprime-foundational-components";

type ViewPublishAppAttribute = {
  attributeType?: string;
  attributeName?: string;
};

type ViewPublishAppItem = {
  role?: string;
  attributes?: ViewPublishAppAttribute[];
};

type ViewPublishAppProps = {
  url?: string;
};

const ViewPublishApp = ({ url = "" }: ViewPublishAppProps) => {
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [compareData, setCompareData] = useState<ViewPublishAppItem[] | null>(
    null,
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);

  const parentColumns = useMemo(
    () => [
      {
        name: "Role Name",
        id: "role",
        type: "FREETEXT",
        size: 650,
      },
    ],
    [],
  );

  const childColumns = useMemo(
    () => [
      {
        name: "Attribute Type",
        id: "attributeType",
        type: "FREETEXT",
        size: 180,
      },
      {
        name: "Attribute Name",
        id: "attributeName",
        type: "FREETEXT",
        size: 300,
      },
    ],
    [],
  );

  const getCompareData = useCallback(async () => {
    try {
      setIsFetchingData(true);
      setCompareData(null);
      setTotalCount(0);
      const response = await axiosInstance.post(url, {
        sortingFilter: {
          filter:
            filters?.length > 0
              ? (filters || [])?.map((item: any) =>
                  formatFilterValue(item?.value, item?.id),
                )
              : [],
          sort: sorting?.map((item: any) => {
            return {
              columnName: item?.id,
              type: item?.desc ? "DESC" : "ASC",
            };
          }),
          page: {
            limit: pagination?.rowsPerPage,
            offset: pagination?.page * pagination?.rowsPerPage,
          },
        },
      });
      setCompareData(response?.data?.data?.roles);
      setTotalCount(response?.data?.data?.totalCount ?? 0);
      setIsFetchingData(false);
    } catch (error: any) {
      triggerToast("Error comparing versions", "error");
      setIsFetchingData(false);
    }
  }, [filters, pagination, sorting, url]);

  useEffect(() => {
    getCompareData();
  }, [getCompareData]);

  useEffect(() => {
    setPagination((prev) => {
      // Avoid unnecessary state updates on initial mount.
      if (prev.page === 0) {
        return prev;
      }

      return {
        ...prev,
        page: 0,
      };
    });
  }, [filters]);

  const gridData: GridTableDataType | any = useMemo(() => {
    const safeCompareData = Array.isArray(compareData) ? compareData : [];

    const rows = safeCompareData.map((item, index) => ({
      id: index + 1,
      role: item?.role || "-",
      subRows: item?.attributes?.length
        ? [
            {
              id: `${index + 1}-details`,
              subComponents: [
                <div
                  key={`${index + 1}-details-grid`}
                  style={{ width: "100%" }}
                >
                  <GridTable
                    style={{ width: "100%" }}
                    title=""
                    data={{
                      columns: childColumns,
                      totalCount: item.attributes.length,
                      rows: item.attributes.map((subItem, subIndex) => ({
                        id: `${index + 1}-${subIndex + 1}`,
                        attributeType: subItem?.attributeType || "-",
                        attributeName: subItem?.attributeName || "-",
                      })),
                    }}
                    isCustomizeColumns={false}
                    isFiltering={false}
                    isSorting={false}
                    isPagination={false}
                    showHeader={false}
                    loading={false}
                  />
                </div>,
              ],
            },
          ]
        : [],
    }));

    return {
      columns: parentColumns,
      totalCount,
      rows,
    };
  }, [childColumns, compareData, parentColumns, totalCount]);

  return (
    <>
      <GridTable
        title="Role Details"
        style={{ minHeight: "10rem" }}
        data={gridData}
        isFiltering={true}
        isSorting={true}
        filters={filters}
        sorting={sorting}
        setSorting={setSorting}
        setFilters={setFilters}
        pagination={pagination}
        isPagination={true}
        loading={isFetchingData}
        showHeader={true}
      />
      <Pagination
        sx={{ width: "100%" }}
        {...{
          ...pagination,
          count: totalCount ?? 0,
          rowsPerPageOptions: [25, 50, 100, 250],
          onPageChange: (_e, number) =>
            setPagination({ ...pagination, page: number }),
          onRowsPerPageChange: (evt) =>
            setPagination({
              page: 0,
              rowsPerPage: Number(evt.target.value ?? 25),
            }),
        }}
      />
    </>
  );
};

export default React.memo(ViewPublishApp, (prevProps, nextProps) => {
  const isUrlUnchanged = prevProps.url === nextProps.url;
  return isUrlUnchanged;
});
